<template>
    <div class="agent-activity-chart w-100">
        <div class="time-ticks">
            <div class="tick" v-for="tick in getTicks()" :class="{ big: tick.isBig, bold: tick.isBold }"></div>
        </div>
        <div class="chart">
            <div class="bars">
                <div
                    v-for="percent in percents"
                    class="bar"
                    :class="{
                        accepting: percent.isAcceptingChat,
                        notaccepting: percent.isNotAcceptingChat,
                        offline: percent.isLoggedOut,
                    }"
                    :style="calculateStyle(percent)"
                    v-tippy="{ followCursor: true, placement: 'top-right' }"
                    :content="calculateTippyContent(percent)"
                />
            </div>
        </div>
        <div class="status d-flex justify-content-center">
            <div class="d-flex mr-2">
                <a-badge color="green" />
                {{ $t("message.acceptingChat") }}:
                <strong class="ml-2">{{ humanizeDuration(status.acceptingChatDuration) }}</strong>
            </div>
            <div class="d-flex mr-2">
                <a-badge color="red" />
                {{ $t("message.notAcceptingChat") }}:
                <strong class="ml-2">{{ humanizeDuration(status.notAcceptingChatDuration) }}</strong>
            </div>
            <div class="d-flex mr-2">
                <a-badge color="gray" />
                {{ $t("message.loggedOut") }}:
                <strong class="ml-2">{{ humanizeDuration(status.loggedOutDuration) }}</strong>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import { calculateStatus, getPercentsFromUserActions } from "./utils";
import { dateToHourFromSecondsShortFormat } from "../../utils/filters";
import log2 from "../../utils/log2";

const totalSecondsInADay = 24 * 60 * 60;
const log = log2("AgentActivityBar");
export default {
    name: "AgentActivityBar",
    props: {
        activities: {
            type: Array,
            required: true,
        },
        lastActivity: {
            type: Object,
            required: false,
        },
        startDay: {
            type: Number,
            required: true,
        },
        hideInactive: {
            type: Boolean,
            required: true,
        },
        hideMinorValues: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {};
    },
    computed: {
        /**
         * @returns {{duration: (*|number), isAcceptingChat: boolean, isLoggedOut: boolean, isOnline: *, isNotAcceptingChat: boolean, percent: number, status: *}[]|*[]|*}
         */
        percents() {
            let percents = getPercentsFromUserActions(this.activities, this.lastActivity, this.startDay);
            log("calculated percents", {
                activities: this.activities,
                lastActivity: this.lastActivity,
                startDay: this.startDay,
                percents,
            });
            if (this.hideMinorValues) {
                const majorValues = percents.filter((percent) => percent.duration > 60);
                if (percents.length - majorValues.length > 0) {
                    log.warn("minor values hidden", { minorValuesDetected: percents.length - majorValues.length });
                }

                percents = majorValues;
            }
            return percents;
        },
        /**
         * @returns {{acceptingChatDuration: Number, notAcceptingChatDuration: Number, loggedOutDuration: Number}}
         */
        status() {
            return calculateStatus(this.percents);
        },
    },
    methods: {
        getTicks() {
            return _.range(25).map((_, index) => {
                return {
                    isBig: index % 4 === 0,
                    isBold: index % 4 !== 0 && index % 2 === 0,
                };
            });
        },

        humanizeDuration(totalSeconds) {
            const duration = moment.duration(totalSeconds, "seconds");
            const hours = duration.hours();
            const minutes = duration.minutes();
            const seconds = duration.seconds();
            return dateToHourFromSecondsShortFormat(totalSeconds, this.$i18n.locale, true);
            // return `${hours}h ${minutes}m ${seconds}s`;
        },
        calculateStyle(item) {
            const { isOnline, status, percent, isAcceptingChat, isNotAcceptingChat, isLoggedOut, duration } = item;
            const colors = {
                accept: "#00ff00",
                not_accept: "#ff0000",
                offline: "rgba(183,183,183,0.71)",
            };
            const style = {
                width: `${percent}%`,
                backgroundColor: isAcceptingChat ? colors.accept : isLoggedOut ? colors.offline : colors.not_accept,
            };
            return style;
        },
        calculateTippyContent(item) {
            const { isOnline, status, percent, isAcceptingChat, isNotAcceptingChat, isLoggedOut, duration, startTS } =
                item;
            const val = dateToHourFromSecondsShortFormat(duration, this.$i18n.locale, true);
            const durationHumanize = startTS
                ? ` from (${moment(startTS).format("LTS")}) to (${moment(startTS)
                      .add(duration, "seconds")
                      .format("LTS")})`
                : "";
            log("calculateTippyContent", { startTS, durationHumanize });
            return isAcceptingChat
                ? `${val} Accepting Chat ${durationHumanize}`
                : isLoggedOut
                ? `${val} offline ${durationHumanize}`
                : `${val} Not Accepting Chat ${durationHumanize}`;
        },
    },
};
</script>

<style scoped lang="scss">
.agent-activity-chart {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    //border: 1px solid red;
    width: 100%;

    padding: 5px;

    .chart {
        height: 40px;
        min-height: 40px;
        max-height: 40px;
        width: 100%;
        //border: 1px solid red;
        display: flex;
        justify-content: center;
        align-items: center;

        .bars {
            display: flex;
            width: 100%;
            height: 10px;
            justify-content: flex-start;

            .bar {
                cursor: pointer;
            }

            .bar.accepting {
                &:hover {
                    border: 1px dashed black;
                    background-color: #00ff00;
                    opacity: 0.8;
                    background: repeating-linear-gradient(-45deg, #00ff00, #00ff00 1px, #fff 1px, #fff 2px);
                }
            }

            .bar.notaccepting {
                &:hover {
                    border: 1px dashed black;
                    background-color: #ff0000;
                    opacity: 0.8;
                    background: repeating-linear-gradient(-45deg, #ff0000, #ff0000 1px, #fff 1px, #fff 2px);
                }
            }

            .bar.offline {
                &:hover {
                    border: 1px dashed black;
                    background-color: rgba(183, 183, 183, 0.71);
                    opacity: 0.8;
                    background: repeating-linear-gradient(
                        -45deg,
                        rgba(183, 183, 183, 0.71),
                        rgba(183, 183, 183, 0.71) 1px,
                        #fff 1px,
                        #fff 2px
                    );
                }
            }
        }
    }

    .status {
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 11px;
    }

    .time-ticks {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        width: 100%;

        .tick {
            width: 1px;
            height: 7px;
            background: #9e9da2;
        }

        .tick.big {
            height: 15px;
        }

        .tick.bold {
            height: 3px;
        }
    }
}
</style>
